import React, {useRef, useState, useEffect} from 'react';
import Container from '@material-ui/core/Container';
import {makeStyles} from "@material-ui/core/styles";

import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Box, 
    TextField,  IconButton, Button, Chip, Tooltip }from '@material-ui/core';

import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import StorageIcon from '@material-ui/icons/Storage';

import { ExcelRenderer } from '../../../utils/excel_parser';
import EnhancedTableCell, {getComparator} from "../../../components/EnhancedTableCell/EnhancedTableCell.comp";

import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {toastWarning} from "../../../utils/utils";
import ImportPreviewModal from "./ImportPreviewModal.comp";
import EditEntityModal from "./EditEntityModal.comp";
import PreRemoveModal from "./PreRemoveModal.comp";

import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    headerInputGroup: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    btnStyle: {
        marginLeft: '10px'
    },
    tr_act: {
        background: '#edf4ff',
    },
    tr_d_act: {
        background: '#f8dede'
    },
    seaerchTextField: {
        marginTop: theme.spacing(4),
    },
    tableScroll: {
        overflow: 'auto'
      },
    table_head_bg: {
        background: '#4285f4'
    },
    table_cell_white: {
        color: 'white',
      },
    cacheChip: {
        marginLeft: theme.spacing(1),
        backgroundColor: '#ff9800',
        color: 'white',
    },
    paginationSummary: {
        padding: theme.spacing(1, 2),
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
}));

export default function DisplayMetaWithImport(props) {

    const {title, modal_title, sheet_name, columns, tableCols, jsonSpRender, tableSpRender, search_query,
        listAPI, postAPI, deleteAPI, updateAPI, preRemoveAPI, populate_locale,
        hideImportData = false, enableManualCreate = false,
        fieldConfig = {}, editModalConfig = {},
        // chunked import for large sheets (optional): rows per upload request
        chunkSize,
        // server-side pagination (optional)
        serverSidePagination, serverPage, serverLimit, serverTotal,
        onServerPageChange, onServerLimitChange,
        // server-side search (optional)
        serverSideSearch, serverSearch, onServerSearchChange, debouncedSearch,
        // load all from cache (optional)
        loadAllCachedAPI, cacheMode, setCacheMode, setCacheData, onExitCacheMode,
    } = props

    const classes = useStyles();

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("__sr#");

    //Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchText, setSearchText] = useState('');
    
    const [openDataPreview, setOpenDataPreview] = useState(false);
    const [previewCols, setPreviewCols] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);

    const [openEditModal, setOpenEntityModal] = useState(false);
    const [editableRow, setEditableRow] = useState(undefined);
    const [formValues, setFormValues] = useState(undefined);

    const [openPreRemoveModal, setOpenPreRemoveModal] = useState(false);

    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState(undefined);

    const [excelDataJson, setExcelDataJson] = useState({});

    const [filteredData, setFilteredData] = useState(tableData);

    const fileInput = useRef();

    const [editableRowSr, setEditableRowSr] = useState(null);

    useEffect( () => {
        setLoading(true);
        loadTableData().then(d => setLoading(false)).catch(e => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch when server page, limit, or debounced search changes (server-side mode)
    useEffect(() => {
        if (!serverSidePagination) return;
        setLoading(true);
        loadTableData().then(d => setLoading(false)).catch(e => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverPage, serverLimit, debouncedSearch]);

    useEffect(() => {
        if (tableData && tableData.rows) {
            if (!serverSideSearch) {
                // Client-side filtering only when NOT in server-side search mode
                if(search_query) {
                    setSearchText(search_query);
                }
                let searchLower = searchText.toLowerCase();
                setFilteredData({
                    ...tableData,
                    rows: tableData.rows.filter((row) =>
                        tableData.columns.some((column) =>
                            String(row[column.field]).toLowerCase().includes(searchLower)
                        )
                    ),
                });
            } else {
                // In server-side search mode, just show all returned rows
                setFilteredData({ ...tableData });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, tableData]);
    
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        const sortDirection = isAsc ? "desc" : "asc"
        setOrder(sortDirection);
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderUpdateEntityModal = (row) => {

        let formValues = {};
        for (let col of columns) {
            const cfg = fieldConfig[col.json_key] || {};
            formValues[col.json_key] = row
                ? row[col.data_key]
                : (cfg.emptyDefault !== undefined ? cfg.emptyDefault : '');
        }

        setFormValues(formValues)
        setEditableRow(row);
        setOpenEntityModal(true);
    }

    const renderCreateEntityModal = () => {
        renderUpdateEntityModal(undefined);
    }

    const renderDeleteEntityModal = async (row) => {

        setEditableRow(row)
        if (preRemoveAPI) {
            setOpenPreRemoveModal(true);
            setEditableRow(row);
        } else {
            await deleteAPIHandler(row.id);
        }

    }

    const prepareAndSetTableData = (list_data) => {
        let data_rows = [];
        for (let [index, row] of list_data.entries()) {
            if (tableSpRender) {
                row = tableSpRender(row);
            }
            if (deleteAPI || updateAPI) {
                row.actions = <div className={'meta-data-table-action-container'}>
                    { updateAPI && <EditIcon
                        onClick={()=>{
                            renderUpdateEntityModal(row);
                        }}
                        className={'meta-data-table-action-btn'} fontSize={'large'} color={'primary'}/> }
                    { deleteAPI && <DeleteIcon
                        onClick={()=> {
                            renderDeleteEntityModal(row);
                        }}
                        className={'meta-data-table-action-btn'} fontSize={'large'} color={'error'}/> }
                </div>;
            }

            row['__sr#'] = serverSidePagination
                ? (serverPage - 1) * serverLimit + index + 1
                : index + 1;
            data_rows.push(row);
        }

        setTableData({
            columns: [
                {
                    label: 'Sr.#',
                    field: '__sr#', sort: 'asc', width: 100
                },
                ...tableCols
            ],
            rows: data_rows
        })

        setFilteredData({
            columns: [
                {
                    label: 'Sr.#',
                    field: '__sr#', sort: 'asc', width: 100
                },
                ...tableCols
            ],
            rows: data_rows
        });
    }

    const loadTableData = async (new_list) => {

        if (new_list) {
            prepareAndSetTableData(new_list)
        } else {

            try {
                const result = await listAPI();
                if (result.data && result.data.metaData) {
                    // populate table data
                    prepareAndSetTableData(result.data.metaData);
                    // reset client-side page on fresh load
                    if (!serverSidePagination) setPage(0);
                } else {
                    // toast some appropriate error
                }
            } catch (e) {
                // error toasted already
            }

        }

    }

    const handleLoadAllCached = async () => {
        if (!loadAllCachedAPI) return;
        setLoading(true);
        try {
            const result = await loadAllCachedAPI();
            if (result && result.data && result.data.metaData) {
                // Store cache data in parent (so listAPI can return it on re-renders)
                if (setCacheData) setCacheData(result.data);
                if (setCacheMode) setCacheMode(true);
                // Directly populate table with all cache records (client-side mode)
                prepareAndSetCacheData(result.data.metaData);
            } else {
                toastWarning('Failed to load cache data.');
            }
        } catch (e) {
            // error toasted already
        }
        setLoading(false);
    };

    const prepareAndSetCacheData = (list_data) => {
        let data_rows = [];
        for (let [index, row] of list_data.entries()) {
            if (tableSpRender) {
                row = tableSpRender(row);
            }
            if (deleteAPI || updateAPI) {
                row.actions = <div className={'meta-data-table-action-container'}>
                    { updateAPI && <EditIcon
                        onClick={()=>{
                            renderUpdateEntityModal(row);
                        }}
                        className={'meta-data-table-action-btn'} fontSize={'large'} color={'primary'}/> }
                    { deleteAPI && <DeleteIcon
                        onClick={()=> {
                            renderDeleteEntityModal(row);
                        }}
                        className={'meta-data-table-action-btn'} fontSize={'large'} color={'error'}/> }
                </div>;
            }
            row['__sr#'] = index + 1;
            data_rows.push(row);
        }
        const cols = [
            { label: 'Sr.#', field: '__sr#', sort: 'asc', width: 100 },
            ...tableCols
        ];
        setTableData({ columns: cols, rows: data_rows });
        setFilteredData({ columns: cols, rows: data_rows });
    };

    const updateAPIHandler = async (data) => {
        setLoading(true);

        const is_create_mode = !editableRow;
        const id = editableRow ? editableRow.id : undefined;
        try {
            let json_data = data;
            for (let col of columns) {
                if (col.data_parser) {
                    json_data[col.json_key] = col.data_parser(json_data[col.json_key]);
                }
            }
            if (jsonSpRender) {
                json_data = jsonSpRender(json_data);
            }

            const res = is_create_mode
                ? await postAPI([json_data])
                : await updateAPI(id, json_data);

            if (is_create_mode) {
                await loadTableData();
                setLoading(false);
                setEditableRow(undefined);
                setFormValues(undefined);
                setOpenEntityModal(false);
                return;
            }

            const table_cols = tableData.columns;
            let table_rows = tableData.rows.map(row=>{
                if (row.id === id ) {
                    let new_row = res.data.entity;
                    if (tableSpRender) {
                        new_row = tableSpRender(new_row);
                    }
                    if (deleteAPI || updateAPI) {
                        new_row.actions = <div className={'meta-data-table-action-container'}>
                            { updateAPI && <EditIcon
                                onClick={()=>{
                                    renderUpdateEntityModal(new_row);
                                }}
                                className={'meta-data-table-action-btn'} fontSize={'large'} color={'primary'}/> }
                            { deleteAPI && <DeleteIcon
                                onClick={()=> {
                                    renderDeleteEntityModal(new_row);
                                }}
                                className={'meta-data-table-action-btn'} fontSize={'large'} color={'error'}/> }
                        </div>;
                    }
                    new_row['__sr#'] = editableRowSr;  // Assign the Sr# back to the row
                    return new_row
                } else {
                    return row
                }
            });

            setTableData({
                columns: table_cols,
                rows: [...table_rows]
            })

            setFilteredData({
                columns: table_cols,
                rows: [...table_rows]
            })

            setLoading(false);
            setEditableRow(undefined);
            setFormValues(undefined);
            setOpenEntityModal(false);
        } catch (e) {
            // error toasted already
            setLoading(false);
        }
    }

    const deleteAPIHandler = async (id) => {
        setLoading(true);
        try {
            const res = await deleteAPI(id);
            console.log('deletion response --> ', res.data);

            if (res.data.removed) {
                const table_cols = tableData.columns;
                let table_rows = tableData.rows.filter(row => row.id !== id);
                setTableData({
                    columns: table_cols,
                    rows: [...table_rows]
                })
                setFilteredData({
                    columns: table_cols,
                    rows: [...table_rows]
                })
            }

            setLoading(false);
        } catch (e) {
            // error toasted already
            setLoading(false);
        }

        setEditableRow(undefined);
        setOpenPreRemoveModal(false);
    }

    const renderFile = (fileObj) => {
        //just pass the fileObj as parameter
        ExcelRenderer(fileObj, sheet_name, (err, resp) => {
            if(err){
                console.log(err);
            }
            else{
                let locale = '';
                if (populate_locale) {
                    try {
                        const f_parts = fileObj.name.split('.');
                        locale = f_parts[0].split('_')[1]
                    } catch (e) {
                        console.log('failed to extract the locale from filename -> ', fileObj.name);
                    }
                }

                let rows = [];
                for (let i=1; i<resp.rows.length; i++) {
                    if (resp.rows[i].length > 0 && resp.rows[i][0] !== '') {
                        let row = [];
                        let is_empty_row = true;

                        for (let col of columns) {
                            let colData = '';
                            if (resp.rows[i][col.key]) {
                                is_empty_row = false;
                                if (col.data_parser) {
                                    colData = col.data_parser(resp.rows[i][col.key]);
                                } else {
                                    if (typeof resp.rows[i][col.key] === 'string') {
                                        colData = resp.rows[i][col.key].trim();
                                    } else {
                                        colData = resp.rows[i][col.key];
                                    }
                                }
                            }

                            row.push(colData);
                        }
                        if (populate_locale) {
                            row[1] = locale;
                        }
                        if (!is_empty_row) rows.push(row);
                    }
                }

                setPreviewCols(columns);
                setPreviewRows(rows);

                // prepare data for json to post of server
                let data = [];
                for (let row of rows) {
                    let row_obj = {};
                    for (const [ci, col] of columns.entries()) {
                        row_obj[col.json_key] = row[ci]
                    }
                    if (jsonSpRender) {
                        row_obj = jsonSpRender(row_obj);
                    }
                    data.push(row_obj);
                }
                setExcelDataJson(data);

                setOpenDataPreview(true);
                setLoading(false);
            }
        }).then(rs=>console.log('finished processing excel file ==> rs => ', rs));
    }

    const fileHandler = (event) => {
        if(event.target.files.length){
            let fileObj = event.target.files[0];
            let fileName = fileObj.name;

            setLoading(true);

            //check for file extension and pass only if it is .xlsx and display error message otherwise
            if(['xlsx', 'csv'].includes(fileName.slice(fileName.lastIndexOf('.')+1))){
                renderFile(fileObj)
            }
            else{
                setLoading(false);
                toastWarning('invalid file type! please upload .xlsx/.csv file only');
            }
        }
    }

    const openFileBrowser = () => {
        fileInput.current.click();
    }

    const reloadPageData = () => {
        setLoading(true);
        loadTableData().then(d => setLoading(false)).catch(e => setLoading(false));
    }

    // Pagination summary: "Showing X – Y of Z records"
    const getPaginationSummary = () => {
        if (serverSidePagination && !cacheMode && serverTotal > 0) {
            const from = (serverPage - 1) * serverLimit + 1;
            const to = Math.min(serverPage * serverLimit, serverTotal);
            return `Showing ${from} – ${to} of ${serverTotal} records`;
        }
        if (cacheMode && filteredData && filteredData.rows) {
            return `Showing all ${filteredData.rows.length} records (from cache)`;
        }
        return null;
    };

    // In cache mode, apply client-side search on the full cached dataset
    const getDisplayData = () => {
        if (cacheMode && serverSideSearch && serverSearch && serverSearch.trim() !== '') {
            const searchLower = serverSearch.toLowerCase();
            return {
                ...filteredData,
                rows: (filteredData?.rows || []).filter((row) =>
                    ['code', 'locale', 'instrument_question'].some(field =>
                        String(row[field] || '').toLowerCase().includes(searchLower)
                    )
                )
            };
        }
        return filteredData;
    };

    const displayData = getDisplayData();

    return (
        <Container component="main" maxWidth='false'>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>

            <div className={classes.headerInputGroup}>
                <Button onClick={reloadPageData} variant="contained"  color="primary"
                        className={classes.btnStyle}>Reload</Button>
                { !hideImportData && <Button onClick={openFileBrowser} variant="contained"  color="primary"
                        className={classes.btnStyle}>Import Data</Button> }
                { enableManualCreate && <Button onClick={renderCreateEntityModal} variant="contained"  color="primary"
                        className={classes.btnStyle}>Add New</Button> }
                { loadAllCachedAPI && !cacheMode && (
                    <Tooltip title="Load the full dataset from server cache (bypasses pagination)">
                        <Button
                            onClick={handleLoadAllCached}
                            variant="contained"
                            style={{ backgroundColor: '#ff9800', color: 'white', marginLeft: '10px' }}
                            startIcon={<StorageIcon />}
                        >
                            Load All from Cache
                        </Button>
                    </Tooltip>
                )}
                { cacheMode && (
                    <Chip
                        icon={<StorageIcon style={{color: 'white'}} />}
                        label="Loaded from Cache"
                        className={classes.cacheChip}
                        onDelete={onExitCacheMode}
                        deleteIcon={<ClearIcon style={{color: 'white'}} />}
                    />
                )}
                <input type="file" hidden onChange={fileHandler} ref={fileInput} onClick={(event)=> { event.target.value = null }} style={{"padding":"10px"}} />
            </div>
            <div className={classes.seaerchTextField}>
            <Box display={'flex'} justifyContent={'flex-end'} mb={2}>
                <TextField
                    variant="outlined"
                    value={serverSideSearch ? (serverSearch || '') : searchText}
                    onChange={(event) => {
                        if (serverSideSearch) {
                            onServerSearchChange && onServerSearchChange(event.target.value);
                        } else {
                            setSearchText(event.target.value);
                        }
                    }}
                    placeholder="Search…"
                    className={classes.textField}
                    InputProps={{
                        startAdornment: <SearchIcon fontSize="small" />,
                        endAdornment: (
                            <IconButton
                                title="Clear"
                                aria-label="Clear"
                                size="small"
                                style={{ visibility: (serverSideSearch ? serverSearch : searchText) ? 'visible' : 'hidden' }}
                                onClick={() => {
                                    if (serverSideSearch) {
                                        onServerSearchChange && onServerSearchChange('');
                                    } else {
                                        setSearchText('');
                                    }
                                }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        ),
                    }}
                />
            </Box>
            </div>
            </div>
            <div className={classes.paper}>
                <h3>{title}</h3>
                { getPaginationSummary() && (
                    <div className={classes.paginationSummary}>{getPaginationSummary()}</div>
                )}
                <div style={{
                    width: '100%'
                }}>
                {
                    displayData?.rows?.length > 0 ?
                        <TableContainer component={Paper}>
                            <div className={classes.tableScroll}>
                            <Table className={classes.table} aria-label="custom pagination table">
                                <TableHead className={classes.table_head_bg}>
                                    {tableData.columns.map((column) => 
                                        column.label !== 'Actions' ? (
                                        <EnhancedTableCell 
                                            key={column.field}
                                            className={classes.table_cell_white} 
                                            id={column.field}
                                            alignment={'left'}
                                            sort={'true'}
                                            orderBy={orderBy}
                                            order={order}
                                            onRequestSort={handleRequestSort}
                                        >
                                            {column.label}
                                        </EnhancedTableCell>
                                        ) : (
                                        <TableCell className={classes.table_cell_white} key={column.field}>
                                            {column.label}
                                        </TableCell>
                                        )
                                    )}
                                </TableHead>
                                <TableBody>
                                    {(serverSidePagination && !cacheMode
                                        ? displayData.rows.sort(getComparator(order, orderBy))
                                        : (rowsPerPage > 0
                                            ? displayData.rows
                                            .sort(getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            : displayData.rows)
                                    ).map((row, index) => (
                                        <TableRow className={classes.tr_act} key={row.id}>
                                            {tableData.columns.map((column) => {
                                                const value = row[column.field];
                                                return (
                                                    <TableCell key={column.field}>
                                                        {column.field === "actions" ?
                                                            <div className={'meta-data-table-action-container'}>
                                                                { updateAPI && <EditIcon
                                                                    onClick={() => {
                                                                        renderUpdateEntityModal(row);
                                                                        // Use the row's stored __sr# (already computed correctly on load)
                                                                        setEditableRowSr(row['__sr#']);
                                                                    }}
                                                                    className={'meta-data-table-action-btn'} fontSize={'large'} color={'primary'}/> }
                                                                { deleteAPI && <DeleteIcon
                                                                    onClick={()=> renderDeleteEntityModal(row)}
                                                                    className={'meta-data-table-action-btn'} fontSize={'large'} color={'error'}/> }
                                                            </div>
                                                            :
                                                            value
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table> 
                            </div>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={serverSidePagination && !cacheMode ? (serverTotal || 0) : (displayData?.rows?.length || 0)}
                                rowsPerPage={serverSidePagination && !cacheMode ? (serverLimit || 10) : rowsPerPage}
                                page={serverSidePagination && !cacheMode ? (serverPage - 1) : page}
                                onPageChange={serverSidePagination && !cacheMode
                                    ? (_, newPage) => onServerPageChange && onServerPageChange(newPage + 1)
                                    : handleChangePage}
                                onRowsPerPageChange={serverSidePagination && !cacheMode
                                    ? (e) => onServerLimitChange && onServerLimitChange(parseInt(e.target.value, 10))
                                    : handleChangeRowsPerPage}
                            />
                        </TableContainer> : <Typography>No rows to display</Typography>
                }
                </div>

            </div>

            <ImportPreviewModal
                open={openDataPreview}
                rows={previewRows}
                cols={previewCols}
                handleClose={ ()=> setOpenDataPreview(false) }
                dataJson={excelDataJson}
                tableReload={loadTableData}
                title={modal_title}
                postAPI={postAPI}
                chunkSize={chunkSize}
            />

            {
                formValues && <EditEntityModal
                    open={openEditModal}
                    columns={columns}
                    formValues={formValues}
                    title={editableRow ? ('Editing ID: ' + editableRow.id) : 'Create New Record'}
                    handleClose={ ()=> {
                        setOpenEntityModal(false);
                        setFormValues(undefined);
                        setEditableRow(undefined);
                    } }
                    updateAPI={updateAPIHandler}
                    fieldConfig={fieldConfig}
                    modalConfig={editModalConfig}
                />
            }

            <PreRemoveModal
                open={openPreRemoveModal}
                title={`Delete Plan: ${
                    editableRow
                        ? (editableRow.label || editableRow.key || editableRow.code || '<N/A>')
                        : '<N/A>'
                }`}
                t_id={editableRow?.id ?? null}
                deleteAPI={deleteAPIHandler}
                preRemoveAPI={preRemoveAPI}
                handleClose={()=>{
                    setOpenPreRemoveModal(false);
                    setEditableRow(undefined);
                }}
            />

            <LoaderWithBackDrop loading={loading} />

        </Container>
    );
}
